<script>
import AppLayout from '@/layouts/AppLayout.vue'
import ReservationDetail from '@/components/reservation/ReservationDetail.vue'
import { ref, onMounted } from 'vue'
import { PlusIcon, XMarkIcon } from '@heroicons/vue/24/outline'
import { getReservationsFn } from '@/services/api/restApiCalls'
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useAuthGlobalStore } from '@/stores/authGlobalStore'
import { useRouter } from 'vue-router'
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner.vue'
import { notify } from 'notiwind'

export default {
  components: {
    AppLayout,
    ReservationDetail,
    LoadingSpinner,
    PlusIcon,
    XMarkIcon
  },
  setup() {
    const authStore = useAuthGlobalStore()
    const restoStore = useRestoGLobalStore()
    const router = useRouter()

    // Check if reservation active
    const isReservationActiveValue = restoStore?.restoInfo?.is_reservation_active
    if (!isReservationActiveValue) {
      router.push('/')
    }
    
    // Reservations data
    const reservationsData = ref([])
    const isLoading = ref(false)
    const showCreateForm = ref(!authStore.isLoggedIn)
    
    // Fetch reservations for logged in users
    const handleGetReservations = async () => {
      if (!authStore.isLoggedIn) return
      isLoading.value = true
      try {
        const reservations_data = await getReservationsFn(1, restoStore?.RESTO_ID)
        reservationsData.value = reservations_data?.reservations || []
      } catch (error) {
        console.error("Failed to fetch reservations", error)
      } finally {
        isLoading.value = false
      }
    }

    const toggleCreateForm = () => {
       showCreateForm.value = !showCreateForm.value
       // Scroll to top if opening
       if (showCreateForm.value) {
         window.scrollTo({ top: 0, behavior: 'smooth' })
       }
    }

    // Refresh list after update/create/cancel from child
    const handleRefresh = async () => {
        if (authStore.isLoggedIn) {
            await handleGetReservations()
            showCreateForm.value = false
        }
    }
    
    onMounted(() => {
      if (authStore.isLoggedIn) {
         handleGetReservations()
      } else {
         showCreateForm.value = true
      }
    })

    return {
      authStore,
      restoStore,
      reservationsData,
      isLoading,
      showCreateForm,
      toggleCreateForm,
      handleRefresh
    }
  }
}
</script>

<template>
  <AppLayout>
    <div
      class="flex flex-col items-center justify-center sm:px-6 px-2 sm:py-8 py-6 mx-auto"
    >
      <div class="w-full max-w-7xl space-y-8">
        
        <!-- Header & Actions -->
        <div v-if="authStore.isLoggedIn" class="flex flex-col sm:flex-row justify-between items-center bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg transition-colors duration-300">
            <div class="mb-4 sm:mb-0 text-center sm:text-left">
              <h2 class="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 transition-colors duration-300">
                My Reservations
              </h2>
              <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Manage your existing reservations
              </p>
            </div>
            
             <button
                class="bg-indigo-600 dark:bg-indigo-500 hover:bg-indigo-700 dark:hover:bg-indigo-600 text-white font-medium py-2.5 px-6 rounded-lg flex items-center justify-center transition-colors shadow-sm"
                @click="toggleCreateForm"
              >
                <div v-if="!showCreateForm" class="flex items-center">
                    <PlusIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                    Reserve Table
                </div>
                <div v-else class="flex items-center">
                    <XMarkIcon class="h-5 w-5 mr-2" aria-hidden="true" />
                    Close Form
                </div>
              </button>
        </div>

        <!-- Create New Reservation Form -->
        <div v-if="showCreateForm" class="scale-100 transition-all duration-300">
             <ReservationDetail
               :reservation="null"
               :isPublic="!authStore.isLoggedIn"
               :restoId="restoStore?.RESTO_ID"
               :showHeader="true"
               @refresh="handleRefresh"
             />
        </div>

        <!-- Reservations List -->
        <div v-if="authStore.isLoggedIn" class="space-y-6">
          
          <!-- Empty State -->
          <div
            v-if="reservationsData.length === 0 && !isLoading && !showCreateForm"
            class="bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center"
          >
            <p class="text-gray-500 dark:text-gray-400 text-lg">No reservations found</p>
            <button @click="toggleCreateForm" class="mt-4 text-indigo-600 dark:text-indigo-400 hover:underline">Book your first table?</button>
          </div>

          <!-- Loading State -->
          <div v-if="isLoading" class="flex justify-center p-8">
              <LoadingSpinner :isLoading="true" />
          </div>
          
          <!-- List of Detail Cards -->
          <div v-else class="grid grid-cols-1 gap-6">
            <ReservationDetail
              v-for="reservation in reservationsData"
              :key="reservation.reservation_id || reservation.id"
              :reservation="reservation"
              :isPublic="false"
              :restoId="restoStore?.RESTO_ID"
              :showHeader="false"
              @refresh="handleRefresh"
            />
          </div>
        </div>

      </div>
    </div>
  </AppLayout>
</template>
